
import React from 'react';
import { Link } from 'react-router-dom';
import { Building, Home, ArrowRight, Award, Clock, Users, Hammer, TreePine } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingCTA from '../components/FloatingCTA';
import HeroCarousel from '../components/HeroCarousel';
import PremiumServiceCard from '../components/PremiumServiceCard';
import WhyChooseCard from '../components/WhyChooseCard';
import Button from '../components/Button';

const Index = () => {
  const heroSlides = [
    {
      id: 1,
      image: 'https://smfuwrmdonlmchctxvjq.supabase.co/storage/v1/object/public/hero-images/image-1.webp',
      alt: 'Modernes Bauhaus'
    },
    {
      id: 2,
      image: 'https://smfuwrmdonlmchctxvjq.supabase.co/storage/v1/object/public/hero-images/image-2.webp',
      alt: 'Hausbau in Arbeit'
    },
    {
      id: 3,
      image: 'https://smfuwrmdonlmchctxvjq.supabase.co/storage/v1/object/public/hero-images/image-3.webp',
      alt: 'Fertiges Familienhaus'
    },
    {
      id: 4,
      image: 'https://smfuwrmdonlmchctxvjq.supabase.co/storage/v1/object/public/hero-images/image-4.webp',
      alt: 'Modernes Wohnhaus'
    }
  ];

  const services = [
    {
      title: "Schlüsselfertig Massivbau",
      description: "Komplettlösungen für Ihren Massivbau von der Planung bis zur schlüsselfertigen Übergabe.",
      icon: Building
    },
    {
      title: "Hybridhäuser - Beton und Holz", 
      description: "Innovative Kombination aus der Stabilität von Beton und der Nachhaltigkeit von Holz.",
      icon: Home
    },
    {
      title: "Holzhäuser",
      description: "Nachhaltige, energieeffiziente Holzhäuser mit höchstem Wohnkomfort.",
      icon: TreePine
    },
    {
      title: "Schlüsselfertige Sanierung",
      description: "Umfassende Sanierung von Wohnungen und Häusern mit modernen Standards.",
      icon: Hammer
    }
  ];

  const whyChooseUs = [
    {
      title: "Deutsche Ingenieurspräzision und Zuverlässigkeit",
      description: "Wir verbinden traditionelle Handwerkskunst mit moderner Ingenieurskunst für höchste Qualität.",
      icon: Award
    },
    {
      title: "Termingerechte und transparente Projektabwicklung", 
      description: "Klare Kommunikation und Einhaltung der vereinbarten Zeitpläne sind für uns selbstverständlich.",
      icon: Clock
    },
    {
      title: "Persönliche Betreuung durch unseren Geschäftsführer",
      description: "Unser Geschäftsführer ist Ihr direkter Ansprechpartner und steht für die Qualität Ihres Projekts.",
      icon: Users
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section with Carousel */}
      <section className="relative">
        <HeroCarousel slides={heroSlides}>
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-8 fade-in-up">
              Ihr zuverlässiger Partner im Bauwesen
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-12 fade-in-up-delay leading-relaxed max-w-3xl mx-auto opacity-90">
              Projektierung - Ausführung - Engineering → alles aus einer Hand
            </p>
          </div>
        </HeroCarousel>
      </section>

      {/* Company Introduction with new gallery image */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8 fade-in-up">
              <h2 className="text-4xl font-bold text-primary-brand">AMMING GmbH</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                AMMING GmbH ist Ihr Spezialist für hochwertige Bauprojekte in Bayern. Mit jahrelanger Erfahrung und technischem Know-how realisieren wir Ihre Bauvorhaben termin- und budgetgerecht. Unser Geschäftsführer Alen Mikic, Master of Engineering, steht persönlich für die Qualität unserer Arbeit.
              </p>
              <div className="pt-4">
                <Link to="/uber-uns">
                  <Button variant="outline" icon={<ArrowRight className="w-4 h-4" />}>
                    Mehr über uns
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative fade-in-up-delay group">
              <div className="relative overflow-hidden rounded-2xl shadow-xl">
                <img 
                  src="https://smfuwrmdonlmchctxvjq.supabase.co/storage/v1/object/public/homepage/gallery-01.JPG"
                  alt="Bauprojekt AMMING GmbH"
                  className="w-full h-80 lg:h-96 object-cover transition-transform duration-500 group-hover:scale-105"
                />
                {/* Subtle overlay filter for better visual appeal */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent opacity-60"></div>
                {/* Enhanced shadow on hover */}
                <div className="absolute inset-0 rounded-2xl shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-16 fade-in-up">
            <h2 className="text-4xl font-bold text-primary-brand mb-6">Unsere Leistungen</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Wir bieten maßgeschneiderte Baulösungen für Ihre individuellen Bedürfnisse. Unser Leistungsspektrum umfasst sowohl Massivbau als auch moderne Holzbau-Konzepte.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 stagger-children">
            {services.map((service, index) => (
              <PremiumServiceCard
                key={index}
                title={service.title}
                description={service.description}
                icon={service.icon}
                delay={index * 100}
              />
            ))}
          </div>

          <div className="text-center pt-12 fade-in-up">
            <Link to="/leistungen">
              <Button variant="primary" icon={<ArrowRight className="w-5 h-5" />}>
                Mehr erfahren
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section-padding bg-primary-brand text-white relative overflow-hidden">
        <div className="container-custom relative z-10">
          <div className="text-center mb-16 fade-in-up">
            <h2 className="text-4xl font-bold mb-6">Warum AMMING GmbH?</h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Unsere Erfahrung und unser Engagement für Qualität machen uns zu Ihrem idealen Baupartner.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 stagger-children">
            {whyChooseUs.map((item, index) => (
              <WhyChooseCard
                key={index}
                title={item.title}
                description={item.description}
                icon={item.icon}
                delay={index * 150}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="section-padding bg-white">
        <div className="container-custom text-center">
          <div className="max-w-3xl mx-auto space-y-8 fade-in-up">
            <h2 className="text-4xl font-bold text-primary-brand">
              Bereit für Ihr nächstes Bauprojekt?
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Lassen Sie uns gemeinsam Ihre Bauträume verwirklichen. Kontaktieren Sie uns für ein unverbindliches Beratungsgespräch.
            </p>
            <div className="pt-8">
              <Link to="/kontakt">
                <Button variant="primary" size="lg" icon={<ArrowRight className="w-5 h-5" />}>
                  Jetzt Kontakt aufnehmen
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingCTA />
    </div>
  );
};

export default Index;
